import { ZuruflexApiError, MAX_UPLOAD_BYTES } from '@zuruflex/api-client';
import type { PartnerDocumentKind } from '@zuruflex/types';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DatePicker,
  FormField,
  Input,
  cn,
} from '@zuruflex/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, ImageUp, X } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';

/** Photograph of a certificate or a scan of one; both are how this arrives. */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'application/pdf'];

/**
 * The kinds that lapse. The API refuses these without an expiry, so the
 * field is marked required here rather than letting the person find out by
 * being sent back.
 */
const EXPIRY_REQUIRED: readonly PartnerDocumentKind[] = [
  'INSURANCE',
  'ROADWORTHY',
  'VEHICLE_REGISTRATION',
];

const MAX_MB = Math.floor(MAX_UPLOAD_BYTES / 1024 / 1024);

/**
 * The API takes a plain `yyyy-MM-dd`, the picker deals in `Date`s, so the
 * two are converted here rather than in the markup.
 *
 * Both go through the local calendar date on purpose: `toISOString()` would
 * hand back the day before for anyone west of UTC, which is how a licence
 * issued on the 1st gets filed as the 31st.
 */
function toIsoDay(date: Date | undefined): string {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function fromIsoDay(value: string): Date | undefined {
  return value ? new Date(`${value}T00:00:00`) : undefined;
}

/** A month this many years either side of today, for the caption dropdowns. */
function yearsFromNow(years: number): Date {
  const now = new Date();
  return new Date(now.getFullYear() + years, now.getMonth(), 1);
}

interface DocumentUploadDialogProps {
  kind: PartnerDocumentKind;
  /** Set when this belongs to one car rather than to the account. */
  vehicleId?: string;
  onClose: (uploaded?: boolean) => void;
}

export function DocumentUploadDialog({ kind, vehicleId, onClose }: DocumentUploadDialogProps) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner } = usePartner();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [number, setNumber] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  /** Set when the chosen image turns out not to decode. */
  const [previewBroken, setPreviewBroken] = useState(false);

  const needsExpiry = EXPIRY_REQUIRED.includes(kind);

  /**
   * A thumbnail of what is about to be sent.
   *
   * The commonest way this upload fails is a photo with a corner cut off or
   * a thumb over the number, and neither is visible from a filename. The
   * object URL is revoked as soon as the file changes, because a dialog
   * opened and closed a few times otherwise leaks every photo tried.
   */
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    setPreviewBroken(false);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  /** Refused here rather than after a round trip and a presign. */
  const accept = (candidate: File | null | undefined): void => {
    if (!candidate) return;
    if (!ACCEPTED.includes(candidate.type)) {
      setError(t('verify.wrongType'));
      return;
    }
    if (candidate.size > MAX_UPLOAD_BYTES) {
      setError(t('verify.tooBig', { max: MAX_MB }));
      return;
    }
    setError(null);
    setFile(candidate);
  };

  const upload = useMutation({
    mutationFn: () => {
      if (!file) {
        throw new ZuruflexApiError(422, { code: 'VALIDATION_ERROR', message: t('verify.pickAFile') });
      }
      return api.uploadPartnerDocument(partner.id, file, {
        kind,
        ...(vehicleId ? { vehicleId } : {}),
        ...(number.trim() ? { number: number.trim() } : {}),
        ...(issuedAt ? { issuedAt } : {}),
        ...(expiresAt ? { expiresAt } : {}),
      });
    },
    onSuccess: async () => {
      // The checklist, the document list and the partner's own KYC status
      // all move on one upload, so all three are refetched rather than the
      // page being left showing a state that is one action out of date.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
        queryClient.invalidateQueries({ queryKey: ['documents'] }),
      ]);
      onClose(true);
    },
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
  });

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    accept(event.dataTransfer.files[0]);
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`common:documentKind.${kind}`)}</DialogTitle>
          <DialogDescription>{t(`verify.kindHint.${kind}`)}</DialogDescription>
        </DialogHeader>

        {error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : null}

        <form
          className="flex flex-col gap-4"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            setError(null);
            upload.mutate();
          }}
        >
          <FormField label={t('verify.file')} hint={t('verify.fileHint')} required>
            {(field) => (
              <div>
                <input
                  {...field}
                  ref={fileInput}
                  type="file"
                  accept={ACCEPTED.join(',')}
                  className="sr-only"
                  onChange={(event) => accept(event.target.files?.[0])}
                />

                {/*
                  A drop target as well as a button. Most of these documents
                  are already a photo sitting on the desktop, and dragging
                  it is one gesture where the file picker is four.
                */}
                {file ? (
                  <div className="flex items-center gap-3 rounded-field border border-border-default bg-surface-sunken p-3">
                    {/* A file the browser cannot decode falls back to the
                        generic mark. Left as an <img>, its alt text spills
                        out of the thumbnail and over the filename. */}
                    {preview && !previewBroken ? (
                      <img
                        src={preview}
                        alt={t('verify.previewAlt')}
                        onError={() => setPreviewBroken(true)}
                        className="size-16 shrink-0 rounded-field bg-surface object-cover"
                      />
                    ) : (
                      <span
                        className="grid size-16 shrink-0 place-items-center rounded-field bg-surface text-text-muted"
                        aria-hidden="true"
                      >
                        <FileText className="size-6" />
                      </span>
                    )}

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{file.name}</p>
                      <p className="mt-0.5 text-xs text-text-muted">{formatSize(file.size)}</p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => fileInput.current?.click()}
                      >
                        {t('common:action.change')}
                      </Button>
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        onClick={() => setFile(null)}
                      >
                        <X className="size-4" />
                        <span className="sr-only">{t('verify.remove')}</span>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    className={cn(
                      'rounded-field border border-dashed p-6 text-center transition-colors',
                      dragging
                        ? 'border-accent bg-accent-soft'
                        : 'border-border-strong bg-surface-sunken',
                    )}
                  >
                    <ImageUp className="mx-auto size-6 text-text-muted" aria-hidden="true" />
                    <p className="mt-2 text-sm text-text-muted">
                      {dragging ? t('verify.dropActive') : t('verify.drop')}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => fileInput.current?.click()}
                    >
                      {t('verify.choose')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </FormField>

          <FormField label={t('verify.number')} hint={t('verify.numberHint')}>
            {(field) => (
              <Input
                {...field}
                value={number}
                onChange={(event) => setNumber(event.target.value)}
              />
            )}
          </FormField>

          {/*
            The Zuruflex date picker rather than <input type="date">. The
            native one draws the browser's own calendar — a blue American
            one over a yellow Rwandan form — and its dd/mm/yyyy behaviour
            changes with the browser's locale rather than with the language
            the partner chose here.
          */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('verify.issued')}>
              {(field) => (
                <DatePicker
                  id={field.id}
                  value={fromIsoDay(issuedAt)}
                  placeholder={t('verify.pickDate')}
                  // Nothing is issued tomorrow, and the dropdowns have to
                  // reach back to a licence given out decades ago.
                  disableFuture
                  startMonth={yearsFromNow(-40)}
                  endMonth={yearsFromNow(0)}
                  onChange={(date) => setIssuedAt(toIsoDay(date))}
                />
              )}
            </FormField>

            {/* An insurance certificate with no expiry cannot be checked
                against the day of a hire, which is the whole point of
                holding it. */}
            <FormField
              label={t('verify.expires')}
              hint={needsExpiry ? t('verify.expiresRequired') : undefined}
              required={needsExpiry}
            >
              {(field) => (
                <DatePicker
                  id={field.id}
                  value={fromIsoDay(expiresAt)}
                  placeholder={t('verify.pickDate')}
                  // A lapsed certificate can still be filed, so the past is
                  // not blocked — only put out of easy reach.
                  startMonth={yearsFromNow(-2)}
                  endMonth={yearsFromNow(20)}
                  onChange={(date) => setExpiresAt(toIsoDay(date))}
                />
              )}
            </FormField>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onClose()}>
              {t('common:action.cancel')}
            </Button>
            <Button type="submit" disabled={!file} isLoading={upload.isPending}>
              {upload.isPending ? t('common:state.working') : t('verify.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Bytes, then kilobytes, then one decimal of megabytes. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}
